import React, { useEffect, useState } from "react";
import FullScreenModal from "@/features/shell/components/FullScreenModal/FullScreenModal";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { SignUpForm, type StepName } from "./SignUpForm";
import { WelcomeOfferBanner } from "./WelcomeOfferBanner";
import { useOtpFlow } from "@/features/auth/hooks/useOtpFlow";
import { telemetry } from "@/utilities/telemetry";
import { useIsLoggedIn } from "@/features/auth/hooks/useIsLoggedIn";
import { useHomeUrl } from "@/hooks/firestore/user/usePreferences";
import { navigate } from "@/utilities/navigate";
import { useIsIOM } from "@/features/auth/hooks/useIsIOM";
import { useIsWelcomeOfferEnabled } from "@/features/auth/hooks/useIsWelcomeOfferEnabled";
import { LeaveSignUpModal } from "./LeaveSignUpModal";

import * as styles from "../Auth.module.scss";

const STEPS: StepName[] = ["Details", "Address", "Identity"] as const;

const SignUpModal = () => {
  const isOpen = useShellStore((state) => state.modal === "signUp");
  const setModal = useShellStore((s) => s.setModal);
  const isIOM = useIsIOM();
  const { isEnabled: isWelcomeOfferEnabled } = useIsWelcomeOfferEnabled();

  const otp = useOtpFlow("signup");
  const reset = otp.store((s) => s.reset);

  const [hideBackButton, setHideBackButton] = useState(false);
  const [currentStep, setCurrentStep] = useState<StepName>("Details");
  const [isLeaveOpen, setIsLeaveOpen] = useState(false);
  const isLoggedIn = useIsLoggedIn();
  const { homeUrl } = useHomeUrl();
  const currentIndex = STEPS.indexOf(currentStep);

  useEffect(() => {
    if (isOpen) {
      // clean OTP state on every fresh open
      reset();
    } else {
      setCurrentStep("Details");
      setHideBackButton(false);
      setIsLeaveOpen(false);
    }
  }, [isOpen]);

  const handleBack = () => {
    if (currentIndex <= 0) return;

    // log the current step before moving away, add 1 to show Step 1-3
    telemetry.signUpBackNavigation({
      fromStep: `Step ${currentIndex + 1}`,
      component: "header_nav",
    });

    setCurrentStep(STEPS[currentIndex - 1]);
  };

  const handleClose = () => {
    if (isLoggedIn) {
      setModal(null);
      return;
    }

    setIsLeaveOpen(true);
  };

  const handleLeave = () => {
    setIsLeaveOpen(false);
    setModal(null);
    navigate(homeUrl || "/");
  };

  return (
    <>
      <FullScreenModal
        isOpen={isOpen}
        title={"Sign Up"}
        onClose={handleClose}
        contentClassName={styles.signUpModal}
        moveTitleOnMobile
        hideSeparator
        withBackButton
        onBackClick={handleBack}
        backAnimatePresence={currentIndex > 0 && !hideBackButton}
      >
        {/* only IOM customers get the welcome offer, never AU */}
        {isIOM && isWelcomeOfferEnabled ? <WelcomeOfferBanner /> : null}
        <SignUpForm
          onSubmit={() => setHideBackButton(true)}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />
      </FullScreenModal>
      <LeaveSignUpModal
        isOpen={isLeaveOpen}
        onContinue={() => setIsLeaveOpen(false)}
        onLeave={handleLeave}
      />
    </>
  );
};

export { SignUpModal };
